import { useMemo } from "react"
import { AggregationType } from "../constants/charts"
import { usePeriod } from "./period"
import { useLastUpdateDate } from "~/ui-rtk/hooks/cube"

import useDate from "~/ui-rtk/hooks/date"
const dayjs = useDate()

type TReturnType = {
  dateRange: [Date, Date]
  compareRange: [Date, Date] | null
}

export type PresetType =
  | "last7Days"
  | "last30Days"
  | "last3Months"
  | "last6Months"
  | "last12Months"

const defaultMaxDate = dayjs().subtract(3, "day").toDate()

// Days subtracted from the end date per preset. Must match the
// DateRangePicker PREDEFINED_PERIODS math so a page-load default produces the
// same range as clicking the corresponding preset in the picker.
const PRESET_DAYS: Record<PresetType, number> = {
  last7Days: 7,
  last30Days: 30,
  last3Months: 90,
  last6Months: 180,
  last12Months: 365,
}

// Calculate dates from preset
const getDatesFromPreset = (
  preset: PresetType,
  maxDate?: Date,
): [Date, Date] => {
  const endDate = maxDate || dayjs().subtract(3, "day").toDate()
  const end = dayjs(endDate).startOf("day").toDate()
  const start = dayjs(end)
    .subtract(PRESET_DAYS[preset] ?? 7, "day")
    .startOf("day")
    .toDate()

  return [start, end]
}

export const useMinMaxDateRange = (
  minDate?: Date,
  maxDate?: Date,
  defaultGranularity?: AggregationType,
  resetPeriodOnGranularityChange = false,
  lastDateMetric?: string,
  defaultPreset?: PresetType,
  defaultStartDate?: Date,
  defaultEndDate?: Date,
): TReturnType => {
  const { lastDate } = useLastUpdateDate(
    lastDateMetric
      ? {
          dateMetric: lastDateMetric,
        }
      : undefined,
  )

  const maxDateWithDefault = useMemo(
    () =>
      maxDate ?? (lastDate ? new Date(lastDate) : undefined) ?? defaultMaxDate,
    [maxDate, lastDate, defaultMaxDate],
  )

  // Calculate default dates from preset or provided dates
  const defaultDates = useMemo<[Date, Date] | null>(() => {
    if (defaultStartDate && defaultEndDate) {
      return [
        dayjs(defaultStartDate).startOf("day").toDate(),
        dayjs(defaultEndDate).startOf("day").toDate(),
      ]
    }
    if (defaultPreset) {
      return getDatesFromPreset(defaultPreset, maxDateWithDefault)
    }
    return null
  }, [defaultPreset, defaultStartDate, defaultEndDate, maxDateWithDefault])

  const { period: dateRange, comparePeriod: compareRange } = usePeriod(
    defaultGranularity,
    resetPeriodOnGranularityChange,
    maxDateWithDefault,
    defaultDates,
  )

  const adjustedStartDate = useMemo<Date>(() => {
    const adjustedStartDate = dayjs(dateRange[0]).startOf("day").toDate()
    if (minDate && adjustedStartDate < minDate) {
      return dayjs(minDate).startOf("day").toDate()
    }
    return adjustedStartDate
  }, [dateRange, minDate])

  const adjustedEndDate = useMemo<Date>(() => {
    const adjustedEndDate = dayjs(dateRange[1]).startOf("day").toDate()
    if (maxDateWithDefault && adjustedEndDate > maxDateWithDefault) {
      return dayjs(maxDateWithDefault).startOf("day").toDate()
    }
    return adjustedEndDate
  }, [dateRange, maxDateWithDefault])

  const period = useMemo<[Date, Date]>(
    () => [adjustedStartDate, adjustedEndDate],
    [adjustedStartDate, adjustedEndDate],
  )
  const compareRangeAdjusted = useMemo<[Date, Date] | null>(() => {
    if (compareRange && compareRange[0] && compareRange[1]) {
      return [dayjs(compareRange[0]).toDate(), dayjs(compareRange[1]).toDate()]
    }
    return null
  }, [compareRange])

  return {
    dateRange: period,
    compareRange: compareRangeAdjusted,
  }
}

export const useYearDateRange = (): [Date, Date] =>
  useMemo<[Date, Date]>(() => {
    const today = new Date()
    const yearAgo = dayjs(today).subtract(1, "year").startOf("day").toDate()
    return [yearAgo, today]
  }, [])
